"use strict";
// tslint:disable
/**
 * Authentication service API
 * A loosely [OAuth 2.0](https://tools.ietf.org/html/rfc6749) compliant authentication service used to obtain bearer tokens for use with the CloudLink Platform micro-service APIs.  # Endpoints - /token is for [OAuth 2.0](https://tools.ietf.org/html/rfc6749) grants. - /assume-role/token can be used to switch account or role (subject to permissions) for a logged in user or a client can obtain access as an anonymous user. - /guest/token can be used to obtain an anonymous user access token with limited access rights (guest level access). - /authorize can be used by a registered client application to initiate an [Open ID Connect](https://openid.net/specs/openid-connect-core-1_0.html) authorization flow. - /policy-test can be used to test a set of resources against a user's access policy. # Supported OAuth 2.0 grants - password - [Resource Owner Password Credentials Flow](https://tools.ietf.org/html/rfc6749#section-4.3) - client_credentials - [Client Credentials Flow](https://tools.ietf.org/html/rfc6749#section-4.4) - refresh_token - [Refreshing an Access Token](https://tools.ietf.org/html/rfc6749#page-47) - authorization_code - [Authorization Code Grant](https://tools.ietf.org/html/rfc6749#section-4.1) These endpoints accept body parameters either in `application/x-www-form-urlencoded` or `application/json` format.  The `/token` endpoint also accepts username/password as a basic auth header.  Input arguments may be passed either as `application/x-www-form-urlencoded` pairs in the request body and/or in a basic auth header. # OAuth 2.0 Grants OAuth 2.0 requests are handled by the `/token` endpoint. ## Login using username and password During user login, the platform checks that the user and account are active and that the password is valid.  The username identifies the user, it is matched against the user's `userId`, `uniqueUserId`, `email`, `mobile` or `loginId` properties.  Example:  `grant_type=password&username=jsmith%40somecompany.com&password=****`  If the request is successful the response is `201 (Created)` and access/refresh tokens are returned.  If the username or password are incorrect then the response is `401 (Unauthorized)`. ## Login using username, password and account ID If the `/token` endpoint returns 409 (Conflict) the username may be present in more than one account.  Provide the account ID to select which account to log the user into:  `grant_type=password&username=jsmith%40somecompany.com&password=****&account_id=4321`  If the request is successful the response is `201 (Created)` and access/refresh tokens are returned.  If the username or password are incorrect then the response is `401 (Unauthorized)`. ## Refresh token to obtain fresh tokens Access tokens are valid for (by default) 1 hour, the platform also returns a fresh token which is valid for (by default) 14 days and can be used to obtain fresh access and refresh tokens.  During refresh, the platform checks that the account and user are still active.  `grant_type=refresh_token&refresh_token=xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`  If the request is successful the response is `201 (Created)` and access/refresh tokens are returned.  If the token is not valid then the response is `401 (Unauthorized)`.  If the user or account is not active then the response is `403 (Forbidden)`. ## Client login using client ID and secret During client login, the platform checks that the client and account are active and that the client secret is valid.  `grant_type=client_credentials&client_id=9552c9497d624b75a7399fdb494abb1e&client_secret=******`  If the request is successful the response is `201 (Created)` and access/refresh tokens are returned.  If the client ID or secret are incorrect then the response is `401 (Unauthorized)`. ## Client login with basic auth header Clients in possession of a client password MAY use the HTTP Basic authentication scheme as defined in [RFC2617](https://tools.ietf.org/html/rfc6749#section-2.3.1) to authenticate with the authorization server.  The client identifier is encoded using the `application/x-www-form-urlencoded` encoding algorithm, and the encoded value is used as the username; the client password is encoded using the same algorithm and used as the password.  header:  `Authorization: 'Basic bXfecHB1UTIyNA=='`  body:  `grant_type=client_credentials`  If the encoded username and password are valid and the request is successful then the response is `201 (Created)` and access/refresh tokens are returned.  If the username or password are invalid then the response is `401 (Unauthorized)`. ## Client requesting anonymous user access by submitting user claims As an extension to OAuth 2.0 the platform supports a convenience feature where a client can request user tokens built using the set of claims provided in the request for anonymous user access.  The term 'anonymous' refers to the fact that the user doesn't actually exist in the platform, but the token can be used to interact with CloudLink platform services as if the user does exist.  Note that not all features will work correctly with this type of token.  Also note that user impersonation is not permitted using this or any other auth feature.  The provided claims must not identify a legitimate user of the platform.  The following claims can be provided along with the client ID and secret:  - principalId* - userId* - uniqueUserId* - email - emailVerified: true or false - phone - phoneVerified: true or false - mobile - mobileVerified: true or false - extension - extensionVerified: true or false - photoUrl: url - profileHeaderUrl: url - languageCode: bcp47 (like:  en-US)  *Note that for the anonymous user feature, one of the identidifer parameters must be present (principalId, userId, uniqueUserId).  Any value can be used as long as there is no user in the platform with that ID, for example a generated UUID would be a good choice.  Example:  `grant_type=client_credentials&client_id=9552c9497d624b75a7399fdb494abb1e&client_secret=******&principalId=13263092-204a-4f74-991d-94ee83b384f9&email=anonymous%40mydomain.com&name=Not%20Real`  If the request is successful the response is `201 (Created)` and the user access/refresh tokens are returned containing the supplied claims.  If the client ID or secret are incorrect then the response is `401 (Unauthorized)`.  If the claims provided actually identify a legitimate platform user then a 403 (Forbidden) response will be returned. # OpenID Connect Flow OpenID Connect authorization is initiated using the `/authorize` endpooint.  In order to use this route, the application must be registered in the platform and a client ID created for the application.  Also any redirect URL(s) which are valid for the application need to be pre-registered.  The application wishing to log in a user and obtain a user token begins by redirecting the user agent (browser) to an authentication portal such as the Mitel global auth portal, providing client ID, response type, redirect URI, state and application name:  ```   HTTP 1.1 302 Found   https://auth.mitel.io/authorize?   client_id=bca4584614724174b8290bdd6c7d5967   &response_type=code   &redirect_uri=https://some.app.mitel.io   &state=abc   &app_name=SomeApp ```  The authentication portal will ask the user for thier username, password and possibly thier account ID if the username is present in multiple accounts.  It will then initiate Open ID authorization by sending a POST request to the /authorize endpoint with the following parameters:  - client_id is the pre-registered client ID for the application. - response_type: code is the only currently supported response type. - username will be used to identify the user. - password is the user's password. - account_id can be provided if the platform returns 409 (Conflict) because the username exists in more that one account. - redirect_uri is the URI to re-direct the user agent to, after successful login.  The platform then logs the user in by generating access and refresh tokens, they are stored ready to be retrieved by the application and an authorization code is returned to the auth portal, to pass along to the calling application.  During user login, the platform checks that the user and account are active and that the password is valid.  The username identifies the user, it is matched against the user's `userId`, `uniqueUserId`, `email`, `mobile` or `loginId` properties.  Example (posted to /authorize):  `client_id=bc81084614724174b8290bdd6c7d5967&response_type=code&username=jsmith%40somecompany.com&password=****&account_id=1234&redirect_uri=https%3A%2F%2Faccounts.dev.mitel.io`  The application will then send the auth code to the `/token` endpoint using the authorization_code flow.  Example (posted to /token):  `grant_type=authorization_code&client_id=bc81084614724174b8290bdd6c7d5967&code=d09fea34-d053-4b80-9bdc-f7d0ffd3b2bd&scope=abc`  Note that auth codes are only stored in the auth service for a period of time.   If the application waits too long, the auth code may have expired.  If the request is successful the response is `201 (Created)` and access/refresh tokens are returned.  If the code is not valid or has expired then the response is `401 (Unauthorized)`.  If the user or account is not active then the response is `403 (Forbidden)`. # Assume Role Feature It is often useful for a partner administrator to switch accounts, to one of the accounts that they administer, as an account administrator, or for an account administrator to assume a user role to limit the effect of unintended requests.  The platform provides an authenticated route for this purpose, served by the `/assume-role/token` endpoint.  A logged in administrator can use this route to issue a token in a different account or with a lower role (depending on thier permissions).  A logged in client can use this route to issue an anonymous user token in a different account or with a lower role (depending on thier permissions).  ## Administrator assuming a user role in a different account Example:  ``` {     \"accountId\": \"1234\",   \"role\": \"USER\" } ```  ## Client assuming an anonymous user role in the same account Example:  ``` {     \"userId\": \"8534c81e-c6ea-4777-a1a2-8163ed318a28\",   \"role\": \"USER\" } ```  If the request is successful the response is `201 (Created)` and access/refresh tokens are returned.  If the authorization token provided as a header is invalid the response is `401 (Unauthorized)`.  If the account is not active, the role is higher than the administrator's current role or the userId matched a legitimate platform user then the response is `403 (Forbidden)` (user impersonation is not allowed by any route). # Guest Access Guest access allows an unauthenticated client to obtain access to an account with extremely limited usage rights.  Guest access must be explicitly enabled by turning on an account policy.  The client simply provides a guest principal ID (which can be anything, a generated UUID would be a good choice) and the name of the user to the `/guest/token` route.  Example:  ``` {     \"principalId\": \"7688c81e-c6ea-4777-a1a2-8163ed318a19\",   \"accountId\": \"1234\",   \"name\": \"Guest User One\" } ```  If the request is successful the response is `201 (Created)` and access/refresh tokens are returned.  If the account is not active, if the principal ID identifies a legitimate platform user or the account is not enabled with the guest access policy then the response is `403 (Forbidden)` (user impersonation is not allowed by any route).
 *
 * OpenAPI spec version: 2017-09-01
 * Contact: cloudlink.support@mitel.io
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually. Blah blah blah
 */
Object.defineProperty(exports, "__esModule", { value: true });
// @ts-ignore
const URLSearchParams = require('url-search-params');
const utils_1 = require("../utils");
const service_requester_1 = require("../service-requester");
// @ts-ignore
const separators = {
    csv: ',',
    ssv: ' ',
    tsv: '\t',
    pipes: '|',
};
/**
 * AuthenticationService
 * @class
 */
class AuthenticationService {
    /**
     * @constructor
     */
    constructor(params = {}) {
        this.serviceRequester = params.serviceRequester || new service_requester_1.ServiceRequester(Object.assign({ service: 'Authentication' }, params));
    }
    /**
    * Creates a client application.  Must be GLOBAL_ADMIN.
    * @summary Create Client Application
    * @param {ApplicationsPostRequest} [body]
    * @throws {RequiredError}
    */
    createClientApplication(params) {
        const path = `/applications`;
        const headers = {};
        const query = {};
        let body = null;
        body = params.body || {};
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Deletes a client's application settings for an account when called by PARTNER_ADMIN or ACCOUNT_ADMIN, or deletes an entire client application for GLOBAL_ADMIN
    * @summary Delete client application
    * @param {string} applicationId The client application ID
    * @param {ApplicationDeleteRequest} body
    * @throws {RequiredError}
    */
    deleteClientApplication(params) {
        // verify required parameter 'applicationId' is not null or undefined
        if (params.applicationId === null || params.applicationId === undefined) {
            throw new utils_1.RequiredError('applicationId', 'Required parameter applicationId was null or undefined when calling deleteClientApplication.');
        }
        // verify required parameter 'body' is not null or undefined
        if (params.body === null || params.body === undefined) {
            throw new utils_1.RequiredError('body', 'Required parameter body was null or undefined when calling deleteClientApplication.');
        }
        const path = `/applications/{applicationId}`
            .replace(`{${'applicationId'}}`, String(params.applicationId));
        const headers = {};
        const query = {};
        let body = null;
        body = params.body || {};
        return this.serviceRequester.request({ method: 'DELETE', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Returns the Application metadata given a client_id for the accountId specified in the token
    * @summary Get Application data corresponding to client_id for a specific account
    * @param {string} applicationId The client application ID
    * @throws {RequiredError}
    */
    getApplicationAccountInfoByClientId(params) {
        // verify required parameter 'applicationId' is not null or undefined
        if (params.applicationId === null || params.applicationId === undefined) {
            throw new utils_1.RequiredError('applicationId', 'Required parameter applicationId was null or undefined when calling getApplicationAccountInfoByClientId.');
        }
        const path = `/applications/{applicationId}`
            .replace(`{${'applicationId'}}`, String(params.applicationId));
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Returns the Application metadata given a client_id
    * @summary Get Application data corresponding to client_id
    * @param {string} clientId
    * @throws {RequiredError}
    */
    getApplicationByClientId(params) {
        // verify required parameter 'clientId' is not null or undefined
        if (params.clientId === null || params.clientId === undefined) {
            throw new utils_1.RequiredError('clientId', 'Required parameter clientId was null or undefined when calling getApplicationByClientId.');
        }
        const path = `/applications`;
        const headers = {};
        const query = {};
        let body = null;
        if (params.clientId !== undefined) {
            query['client_id'] = params.clientId;
        }
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Updates a client application for the account in the token.  Must be ACCOUNT_ADMIN or higher.
    * @summary Update Client Application
    * @param {string} applicationId The client application ID
    * @param {ApplicationPutRequest} [body]
    * @throws {RequiredError}
    */
    updateClientApplication(params) {
        // verify required parameter 'applicationId' is not null or undefined
        if (params.applicationId === null || params.applicationId === undefined) {
            throw new utils_1.RequiredError('applicationId', 'Required parameter applicationId was null or undefined when calling updateClientApplication.');
        }
        const path = `/applications/{applicationId}`
            .replace(`{${'applicationId'}}`, String(params.applicationId));
        const headers = {};
        const query = {};
        let body = null;
        body = params.body || {};
        return this.serviceRequester.request({ method: 'PUT', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Create an Authorization Code for OpenId OAuth 2.0 Flow
    * @summary Request to create an Authorize Code
    * @param {string} clientId Application Identifier
    * @param {string} responseType return type
    * @param {string} [scope] Allow access to particular scope. Multiple scopes can be used by creating a space-delimited, case sensitive list of scope values.   Supported scope values(description): gauth(Issues a Connect Cloud Token in addtion to a CloudLink Token)
    * @param {string} [username] username of user
    * @param {string} [password] password for user
    * @param {string} [accountId] Account identifier for the user. If the username exists in multiple accounts, the Account Id will be used to uniquely identify a user.
    * @param {string} [redirectUri] uri which the code will eventually be returned to
    * @param {string} [nonce] nonce for the session state
    * @param {string} [sessionState] session state
    * @param {string} [codeChallengeMethod] PKCE code verifier transformation method. Only S256 is supported.
    * @param {string} [codeChallenge] PKCE code challenge
    * @param {string} [idTokenHint] the token to exchange for an authorization code
    * @param {string} [targetClientId] the destination client application identifier meant to receive this authorization code
    * @throws {RequiredError}
    */
    createAuthorizationCode(params) {
        // verify required parameter 'clientId' is not null or undefined
        if (params.clientId === null || params.clientId === undefined) {
            throw new utils_1.RequiredError('clientId', 'Required parameter clientId was null or undefined when calling createAuthorizationCode.');
        }
        // verify required parameter 'responseType' is not null or undefined
        if (params.responseType === null || params.responseType === undefined) {
            throw new utils_1.RequiredError('responseType', 'Required parameter responseType was null or undefined when calling createAuthorizationCode.');
        }
        const path = `/authorize`;
        const headers = {};
        const query = {};
        let body = null;
        const formParams = new URLSearchParams();
        if (params.clientId !== undefined) {
            formParams.set('client_id', params.clientId);
        }
        if (params.responseType !== undefined) {
            formParams.set('response_type', params.responseType);
        }
        if (params.scope !== undefined) {
            formParams.set('scope', params.scope);
        }
        if (params.username !== undefined) {
            formParams.set('username', params.username);
        }
        if (params.password !== undefined) {
            formParams.set('password', params.password);
        }
        if (params.accountId !== undefined) {
            formParams.set('account_id', params.accountId);
        }
        if (params.redirectUri !== undefined) {
            formParams.set('redirect_uri', params.redirectUri);
        }
        if (params.nonce !== undefined) {
            formParams.set('nonce', params.nonce);
        }
        if (params.sessionState !== undefined) {
            formParams.set('session_state', params.sessionState);
        }
        if (params.codeChallengeMethod !== undefined) {
            formParams.set('code_challenge_method', params.codeChallengeMethod);
        }
        if (params.codeChallenge !== undefined) {
            formParams.set('code_challenge', params.codeChallenge);
        }
        if (params.idTokenHint !== undefined) {
            formParams.set('id_token_hint', params.idTokenHint);
        }
        if (params.targetClientId !== undefined) {
            formParams.set('target_client_id', params.targetClientId);
        }
        headers['content-type'] = 'application/x-www-form-urlencoded';
        body = formParams.toString();
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Retrieve the specification in swagger format for this api
    * @summary Get spec
    * @throws {RequiredError}
    */
    getSpec(params) {
        const path = `/_spec`;
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Creates an SSO identity provider.  Must be ACCOUNT_ADMIN or higher.
    * @summary Create identity provider
    * @param {IdentityProviderPostRequest} body
    * @throws {RequiredError}
    */
    createIdentityProvider(params) {
        // verify required parameter 'body' is not null or undefined
        if (params.body === null || params.body === undefined) {
            throw new utils_1.RequiredError('body', 'Required parameter body was null or undefined when calling createIdentityProvider.');
        }
        const path = `/identityProviders`;
        const headers = {};
        const query = {};
        let body = null;
        body = params.body || {};
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Deletes an SSO identity provider
    * @summary Delete identity provider
    * @param {string} identityProviderId Identity Provider ID - accountId of the account of the identity provider
    * @throws {RequiredError}
    */
    deleteIdentityProvider(params) {
        // verify required parameter 'identityProviderId' is not null or undefined
        if (params.identityProviderId === null || params.identityProviderId === undefined) {
            throw new utils_1.RequiredError('identityProviderId', 'Required parameter identityProviderId was null or undefined when calling deleteIdentityProvider.');
        }
        const path = `/identityProviders/{identityProviderId}`
            .replace(`{${'identityProviderId'}}`, String(params.identityProviderId));
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'DELETE', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Retrieves the identity provider for SSO for the account.  Currently an account can have only one configured identity provier. This is only available for ACCOUNT_ADMIN or higher role.
    * @summary Get identity provider
    * @param {string} identityProviderId Identity Provider ID - accountId of the account of the identity provider
    * @throws {RequiredError}
    */
    getIdentityProvider(params) {
        // verify required parameter 'identityProviderId' is not null or undefined
        if (params.identityProviderId === null || params.identityProviderId === undefined) {
            throw new utils_1.RequiredError('identityProviderId', 'Required parameter identityProviderId was null or undefined when calling getIdentityProvider.');
        }
        const path = `/identityProviders/{identityProviderId}`
            .replace(`{${'identityProviderId'}}`, String(params.identityProviderId));
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Retrieves the identity providers for SSO for the account.  Currently an account can have only one configured identity provier. This is only available for ACCOUNT_ADMIN or higher role.
    * @summary Get identity providers
    * @throws {RequiredError}
    */
    getIdentityProviders(params) {
        const path = `/identityProviders`;
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Implements the OpenID Connect RP-initiated Logout
    * @summary Get Logout URL for Identity Provider
    * @param {string} username
    * @param {string} [accountId]
    * @throws {RequiredError}
    */
    saml2Logout(params) {
        // verify required parameter 'username' is not null or undefined
        if (params.username === null || params.username === undefined) {
            throw new utils_1.RequiredError('username', 'Required parameter username was null or undefined when calling saml2Logout.');
        }
        const path = `/saml2/logout`;
        const headers = {};
        const query = {};
        let body = null;
        const formParams = new URLSearchParams();
        if (params.username !== undefined) {
            formParams.set('username', params.username);
        }
        if (params.accountId !== undefined) {
            formParams.set('account_id', params.accountId);
        }
        headers['content-type'] = 'application/x-www-form-urlencoded';
        body = formParams.toString();
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Exchange SAML claims from an Identity Provider and issue an OID Connect authorization code
    * @summary Process SAML claims from 3rd party Identity Providers
    * @param {string} accountId
    * @param {string} sAMLResponse
    * @param {string} relayState
    * @throws {RequiredError}
    */
    saml2Response(params) {
        // verify required parameter 'accountId' is not null or undefined
        if (params.accountId === null || params.accountId === undefined) {
            throw new utils_1.RequiredError('accountId', 'Required parameter accountId was null or undefined when calling saml2Response.');
        }
        // verify required parameter 'sAMLResponse' is not null or undefined
        if (params.sAMLResponse === null || params.sAMLResponse === undefined) {
            throw new utils_1.RequiredError('sAMLResponse', 'Required parameter sAMLResponse was null or undefined when calling saml2Response.');
        }
        // verify required parameter 'relayState' is not null or undefined
        if (params.relayState === null || params.relayState === undefined) {
            throw new utils_1.RequiredError('relayState', 'Required parameter relayState was null or undefined when calling saml2Response.');
        }
        const path = `/saml2/{accountId}/cs`
            .replace(`{${'accountId'}}`, String(params.accountId));
        const headers = {};
        const query = {};
        let body = null;
        const formParams = new URLSearchParams();
        if (params.sAMLResponse !== undefined) {
            formParams.set('SAMLResponse', params.sAMLResponse);
        }
        if (params.relayState !== undefined) {
            formParams.set('RelayState', params.relayState);
        }
        headers['content-type'] = 'application/x-www-form-urlencoded';
        body = formParams.toString();
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Generate a SAML Request for a user
    * @summary Initiate SAML2.0 based authentication
    * @param {string} username
    * @param {string} clientId
    * @param {string} redirectUri
    * @param {string} [accountId]
    * @param {string} [scope]
    * @param {boolean} [forceAuthn]
    * @param {string} [state]
    * @param {string} [codeChallenge]
    * @param {string} [codeChallengeMethod]
    * @throws {RequiredError}
    */
    saml2Start(params) {
        // verify required parameter 'username' is not null or undefined
        if (params.username === null || params.username === undefined) {
            throw new utils_1.RequiredError('username', 'Required parameter username was null or undefined when calling saml2Start.');
        }
        // verify required parameter 'clientId' is not null or undefined
        if (params.clientId === null || params.clientId === undefined) {
            throw new utils_1.RequiredError('clientId', 'Required parameter clientId was null or undefined when calling saml2Start.');
        }
        // verify required parameter 'redirectUri' is not null or undefined
        if (params.redirectUri === null || params.redirectUri === undefined) {
            throw new utils_1.RequiredError('redirectUri', 'Required parameter redirectUri was null or undefined when calling saml2Start.');
        }
        const path = `/saml2/start`;
        const headers = {};
        const query = {};
        let body = null;
        const formParams = new URLSearchParams();
        if (params.username !== undefined) {
            formParams.set('username', params.username);
        }
        if (params.clientId !== undefined) {
            formParams.set('client_id', params.clientId);
        }
        if (params.redirectUri !== undefined) {
            formParams.set('redirect_uri', params.redirectUri);
        }
        if (params.accountId !== undefined) {
            formParams.set('account_id', params.accountId);
        }
        if (params.scope !== undefined) {
            formParams.set('scope', params.scope);
        }
        if (params.forceAuthn !== undefined) {
            formParams.set('force_authn', params.forceAuthn);
        }
        if (params.state !== undefined) {
            formParams.set('state', params.state);
        }
        if (params.codeChallenge !== undefined) {
            formParams.set('code_challenge', params.codeChallenge);
        }
        if (params.codeChallengeMethod !== undefined) {
            formParams.set('code_challenge_method', params.codeChallengeMethod);
        }
        headers['content-type'] = 'application/x-www-form-urlencoded';
        body = formParams.toString();
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Updates the identity provider for SSO for the account.  This is only available for ACCOUNT_ADMIN or higher role.
    * @summary Update identity provider
    * @param {string} identityProviderId Identity Provider ID - accountId of the account of the identity provider
    * @param {IdentityProviderPutRequest} body
    * @throws {RequiredError}
    */
    updateIdentityProvider(params) {
        // verify required parameter 'identityProviderId' is not null or undefined
        if (params.identityProviderId === null || params.identityProviderId === undefined) {
            throw new utils_1.RequiredError('identityProviderId', 'Required parameter identityProviderId was null or undefined when calling updateIdentityProvider.');
        }
        // verify required parameter 'body' is not null or undefined
        if (params.body === null || params.body === undefined) {
            throw new utils_1.RequiredError('body', 'Required parameter body was null or undefined when calling updateIdentityProvider.');
        }
        const path = `/identityProviders/{identityProviderId}`
            .replace(`{${'identityProviderId'}}`, String(params.identityProviderId));
        const headers = {};
        const query = {};
        let body = null;
        body = params.body || {};
        return this.serviceRequester.request({ method: 'PUT', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Evaluate an access policy and get a list of effects that indicate whether the specified action(s) are allowed on the specified resource(s)
    * @summary Evaluate a policy against a set of actions and resources
    * @param {PolicyTestRequest} body
    * @throws {RequiredError}
    */
    evaluatePolicy(params) {
        // verify required parameter 'body' is not null or undefined
        if (params.body === null || params.body === undefined) {
            throw new utils_1.RequiredError('body', 'Required parameter body was null or undefined when calling evaluatePolicy.');
        }
        const path = `/policy-test`;
        const headers = {};
        const query = {};
        let body = null;
        body = params.body || {};
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Gets the sso providers configured on the platform. Will return the default providers unless an alternate profile parameter is provided
    * @summary Gets the sso providers configured on the platform
    * @param {string} [clientId] Request a specific set of sso providers based on the supported client_id. Defaults to &#39;default&#39; on the platform side.
    * @throws {RequiredError}
    */
    getSsoProviders(params) {
        const path = `/sso`;
        const headers = {};
        const query = {};
        let body = null;
        if (params.clientId !== undefined) {
            query['client_id'] = params.clientId;
        }
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * This starts the authentication flow with a third party sso provider. This link is meant to be used in an <a href... tag vs an api call as the result is a 302 redirect. Clients typically bind this link to a button click. Note the required parameters to start the flow
    * @summary Starts a login flow to a configured SSO provider.
    * @param {string} providerId The sso provider to trigger the flow, for instance google or facebook
    * @param {string} clientId A registered client_id / application in the admin system.
    * @param {string} redirectUri The url to redirect back to upon success or failure. Success redirects look like &lt;redirect_uri&gt;?code&#x3D;abc123... and failure redirects look like &lt;redirect_uri&gt;?error&#x3D;errorCode?info&#x3D;info
    * @param {string} [action] The action to perform upon successful validation by an SSO provider. Login does an authenticate against our system, sign-up will create a user in an account and link will allow this third party to be linked to an existing identity. Guest will allow a guest access on the given account. Defaults to &#39;login&#39;.  Link is not supported yet.
    * @param {string} [scopes] Additional scopes to include in the sign-on process. Comma separated
    * @param {string} [accountId] An optional accountId or partnerId to help with the sign-up / login flows. If this is known it should be passed to prevent conflict errors. It is required when using a &#39;link&#39; or &#39;guest&#39;
    * @param {string} [userId] userId is REQUIRED when dealing with the &#39;link&#39; flow only. If you are linking a user account you must provide and accountId, userId, and accessCode to link.
    * @param {string} [accessCode] accessCode is REQUIRED when dealing with the &#39;link&#39; flow only. If you are linking a user account you must provide and accountId, userId, and accessCode to link.
    * @throws {RequiredError}
    */
    loginSsoProvider(params) {
        // verify required parameter 'providerId' is not null or undefined
        if (params.providerId === null || params.providerId === undefined) {
            throw new utils_1.RequiredError('providerId', 'Required parameter providerId was null or undefined when calling loginSsoProvider.');
        }
        // verify required parameter 'clientId' is not null or undefined
        if (params.clientId === null || params.clientId === undefined) {
            throw new utils_1.RequiredError('clientId', 'Required parameter clientId was null or undefined when calling loginSsoProvider.');
        }
        // verify required parameter 'redirectUri' is not null or undefined
        if (params.redirectUri === null || params.redirectUri === undefined) {
            throw new utils_1.RequiredError('redirectUri', 'Required parameter redirectUri was null or undefined when calling loginSsoProvider.');
        }
        const path = `/sso/{providerId}`
            .replace(`{${'providerId'}}`, String(params.providerId));
        const headers = {};
        const query = {};
        let body = null;
        if (params.clientId !== undefined) {
            query['client_id'] = params.clientId;
        }
        if (params.redirectUri !== undefined) {
            query['redirect_uri'] = params.redirectUri;
        }
        if (params.action !== undefined) {
            query['action'] = params.action;
        }
        if (params.scopes !== undefined) {
            query['scopes'] = params.scopes;
        }
        if (params.accountId !== undefined) {
            query['accountId'] = params.accountId;
        }
        if (params.userId !== undefined) {
            query['userId'] = params.userId;
        }
        if (params.accessCode !== undefined) {
            query['accessCode'] = params.accessCode;
        }
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * This is the route that the third party sso provider will call back into. NOT meant to be used directly by client apis.
    * @summary The callback route used by the oauth sso provider.
    * @param {string} providerId
    * @throws {RequiredError}
    */
    loginSsoProviderCallback(params) {
        // verify required parameter 'providerId' is not null or undefined
        if (params.providerId === null || params.providerId === undefined) {
            throw new utils_1.RequiredError('providerId', 'Required parameter providerId was null or undefined when calling loginSsoProviderCallback.');
        }
        const path = `/sso/{providerId}/callback`
            .replace(`{${'providerId'}}`, String(params.providerId));
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Provides the status of the microservice
    * @summary Get microservice status
    * @throws {RequiredError}
    */
    getStatus(params) {
        const path = `/_status`;
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Request a guest bearer token
    * @summary Post /guest/token -> authenticate for guest access
    * @param {string} [accountId] The account for which the guest token will be issued.  Provide this value if known.
    * @param {string} [accessCode] The meeting access code which will be used to determine the account for which the guest token will be issued.
    * @param {string} [principalId] How this client will be identified within the system (e.g. when chat messages get stored), cannot conflict with an existing principalId.  One will be generated if not provided
    * @param {string} [name] The name of this guest user
    * @param {string} [guestEmail] The email address of this guest user
    * @param {string} [clientId] The client application requesting this guest token
    * @throws {RequiredError}
    */
    createGuestToken(params) {
        const path = `/guest/token`;
        const headers = {};
        const query = {};
        let body = null;
        const formParams = new URLSearchParams();
        if (params.accountId !== undefined) {
            formParams.set('accountId', params.accountId);
        }
        if (params.accessCode !== undefined) {
            formParams.set('accessCode', params.accessCode);
        }
        if (params.principalId !== undefined) {
            formParams.set('principalId', params.principalId);
        }
        if (params.name !== undefined) {
            formParams.set('name', params.name);
        }
        if (params.guestEmail !== undefined) {
            formParams.set('guestEmail', params.guestEmail);
        }
        if (params.clientId !== undefined) {
            formParams.set('client_id', params.clientId);
        }
        headers['content-type'] = 'application/x-www-form-urlencoded';
        body = formParams.toString();
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Request an access token (OAuth 2.0 with extensions)
    * @summary Post /token -> authenticate
    * @param {string} grantType This is used to specify the type of OAuth 2.0 flow that is being requested.
    * @param {string} [accountId] The accountId or accountNumber registered on the platform. Required if the username is present in more than one account.
    * @param {string} [username] Used when the grant_type &#x3D; &#39;password&#39;, username &amp; password are the login credentials for the user
    * @param {string} [password] Used when the grant_type &#x3D; &#39;password&#39;, username &amp; password are the login credentials for the user
    * @param {string} [clientId] Used when the grant_type &#x3D; &#39;client_credentials&#39;, client_id &amp; client_secret are the login credentials for the client
    * @param {string} [clientSecret] Used when the grant_type &#x3D; &#39;client_credentials&#39;, client_id &amp; client_secret are the login credentials for the client
    * @param {string} [token] (Deprecated) Use refresh_token instead. Used when the grant_type &#x3D; &#39;refresh_token&#39;, this is the refresh token which will be used to generate a fresh set of tokens
    * @param {string} [refreshToken] Used when the grant_type &#x3D; &#39;refresh_token&#39;, this is the refresh token which will be used to generate a fresh set of tokens
    * @param {string} [code] Used when the grant_type &#x3D; &#39;authorization_code&#39; which is the code that was sent in the authorization code response from Open ID Connect
    * @param {string} [redirectUri] Used when the grant_type &#x3D; &#39;authorization_code&#39; must match the redirect_uri in the create authorization_code request if provided
    * @param {string} [scope] Request for access to additional resource(s). Space delimited scope identifiers (eg: gauth). For grant_type &#x3D; &#39;authorization_code&#39;, the scope items must match one of the scopes that was provided in create authorization_code request.   Supported scope values: openid, gauth, abc, profile, read_write
    * @param {string} [principalId] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim, must not identify a real user
    * @param {string} [uniqueUserId] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim, must not identify a real user
    * @param {string} [userId] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim, must not identify a real user
    * @param {string} [name] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim.
    * @param {string} [role] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim. The role cannot be higher than the client&#39;s own role
    * @param {string} [email] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim (verified), must not identify a real user
    * @param {string} [emailVerified] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with an unverified email.
    * @param {string} [phone] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim (verified).
    * @param {string} [phoneVerified] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with an unverified phone
    * @param {string} [mobile] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim (verified), must not identify a real user
    * @param {string} [mobileVerified] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with an unverified mobile
    * @param {string} [extension] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim (verified)
    * @param {string} [extensionVerified] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with an unverified extension
    * @param {string} [photoUrl] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim
    * @param {string} [profileHeaderUrl] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim
    * @param {string} [languageCode] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim
    * @param {string} [loginId] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim (verified), must not identify a real user
    * @param {string} [siteId] Used when the grant_type &#x3D; &#39;client_credentials&#39;, will issue an anonymous user token with this claim
    * @param {string} [codeVerifier] PKCE code verifier
    * @throws {RequiredError}
    */
    createToken(params) {
        // verify required parameter 'grantType' is not null or undefined
        if (params.grantType === null || params.grantType === undefined) {
            throw new utils_1.RequiredError('grantType', 'Required parameter grantType was null or undefined when calling createToken.');
        }
        const path = `/token`;
        const headers = {};
        const query = {};
        let body = null;
        const formParams = new URLSearchParams();
        if (params.grantType !== undefined) {
            formParams.set('grant_type', params.grantType);
        }
        if (params.accountId !== undefined) {
            formParams.set('account_id', params.accountId);
        }
        if (params.username !== undefined) {
            formParams.set('username', params.username);
        }
        if (params.password !== undefined) {
            formParams.set('password', params.password);
        }
        if (params.clientId !== undefined) {
            formParams.set('client_id', params.clientId);
        }
        if (params.clientSecret !== undefined) {
            formParams.set('client_secret', params.clientSecret);
        }
        if (params.token !== undefined) {
            formParams.set('token', params.token);
        }
        if (params.refreshToken !== undefined) {
            formParams.set('refresh_token', params.refreshToken);
        }
        if (params.code !== undefined) {
            formParams.set('code', params.code);
        }
        if (params.redirectUri !== undefined) {
            formParams.set('redirect_uri', params.redirectUri);
        }
        if (params.scope !== undefined) {
            formParams.set('scope', params.scope);
        }
        if (params.principalId !== undefined) {
            formParams.set('principalId', params.principalId);
        }
        if (params.uniqueUserId !== undefined) {
            formParams.set('uniqueUserId', params.uniqueUserId);
        }
        if (params.userId !== undefined) {
            formParams.set('userId', params.userId);
        }
        if (params.name !== undefined) {
            formParams.set('name', params.name);
        }
        if (params.role !== undefined) {
            formParams.set('role', params.role);
        }
        if (params.email !== undefined) {
            formParams.set('email', params.email);
        }
        if (params.emailVerified !== undefined) {
            formParams.set('emailVerified', params.emailVerified);
        }
        if (params.phone !== undefined) {
            formParams.set('phone', params.phone);
        }
        if (params.phoneVerified !== undefined) {
            formParams.set('phoneVerified', params.phoneVerified);
        }
        if (params.mobile !== undefined) {
            formParams.set('mobile', params.mobile);
        }
        if (params.mobileVerified !== undefined) {
            formParams.set('mobileVerified', params.mobileVerified);
        }
        if (params.extension !== undefined) {
            formParams.set('extension', params.extension);
        }
        if (params.extensionVerified !== undefined) {
            formParams.set('extensionVerified', params.extensionVerified);
        }
        if (params.photoUrl !== undefined) {
            formParams.set('photoUrl', params.photoUrl);
        }
        if (params.profileHeaderUrl !== undefined) {
            formParams.set('profileHeaderUrl', params.profileHeaderUrl);
        }
        if (params.languageCode !== undefined) {
            formParams.set('languageCode', params.languageCode);
        }
        if (params.loginId !== undefined) {
            formParams.set('loginId', params.loginId);
        }
        if (params.siteId !== undefined) {
            formParams.set('siteId', params.siteId);
        }
        if (params.codeVerifier !== undefined) {
            formParams.set('code_verifier', params.codeVerifier);
        }
        headers['content-type'] = 'application/x-www-form-urlencoded';
        body = formParams.toString();
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Request a bearer token with assume role
    * @summary Post /token -> authenticate
    * @param {AssumeRoleTokenPostRequest} body
    * @throws {RequiredError}
    */
    createTokenWithAssumedRole(params) {
        // verify required parameter 'body' is not null or undefined
        if (params.body === null || params.body === undefined) {
            throw new utils_1.RequiredError('body', 'Required parameter body was null or undefined when calling createTokenWithAssumedRole.');
        }
        const path = `/assume-role/token`;
        const headers = {};
        const query = {};
        let body = null;
        body = params.body || {};
        return this.serviceRequester.request({ method: 'POST', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
    /**
    * Used to validate/decode the passed in token to the authorization header.  Can be used as a Who Am I call or a simple auth check.
    * @summary Get token / validate
    * @throws {RequiredError}
    */
    getClaims(params) {
        const path = `/token`;
        const headers = {};
        const query = {};
        let body = null;
        return this.serviceRequester.request({ method: 'GET', path, query, headers, body, options: params.options })
            .then(response => response.body);
    }
}
exports.AuthenticationService = AuthenticationService;
//# sourceMappingURL=authentication-service.g.js.map